import { ApolloClient, createHttpLink, gql, InMemoryCache } from '@apollo/client';
import {} from '@apollo/client/utilities';
import { DocumentNode } from 'graphql';

import { clearGraphQLErrorMessage } from 'common/util/string';
import * as K from 'common/constants';
import log from 'common/ScalyrLogger';

/**
 * Middleware to apply the `opname` url param
 */
function customFetch(uri: string, options: RequestInit): Promise<Response> {
  return fetch(`${uri}?opname=${JSON.parse(options.body as string).operationName}`, options);
}

export const clientLite = new ApolloClient({
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      fetchPolicy: 'no-cache',
    },
    mutate: {
      fetchPolicy: 'no-cache',
    },
  },
  link: createHttpLink({
    uri: K.API_ENDPOINT,
    fetch: customFetch,
    headers: {
      'X-Csrf-Token': window.xsrfToken ?? '',
    },
  }),
});

interface RequestOptionsBase {
  variables?: Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
}
interface RequestOptionsQuery extends RequestOptionsBase {
  query: DocumentNode;
  mutation?: never;
}
interface RequestOptionsMutation extends RequestOptionsBase {
  query?: never;
  mutation: DocumentNode;
}
type RequestOptions = RequestOptionsQuery | RequestOptionsMutation;

/**
 * Make a one time GraphQL request avoiding cache and middleware used in the core application
 *  can be used in the places outside of the Apollo client context
 */
const gqlRequest = async <ReturnType>({ query, mutation, variables }: RequestOptions): Promise<ReturnType> => {
  try {
    let result;

    if (query) {
      result = await clientLite.query({
        query,
        variables,
      });
    }

    if (mutation) {
      result = await clientLite.mutate({
        mutation,
        variables,
      });
    }

    // @ts-expect-error
    return result.data;
  } catch (err) {
    const resolvedError = new Error(clearGraphQLErrorMessage(err.message));

    log.error(resolvedError);

    // To be handled by the caller
    throw new Error(clearGraphQLErrorMessage(err.message));
  }
};

export { gql, gqlRequest };
